import { Project } from "projen";
import { GitHub } from "projen/lib/github";
import { JobStep } from "projen/lib/github/workflows-model";

/**
 * Name of the `Upload artifact` step projen emits in the build job.
 *
 * @see {@link UPLOAD_ARTIFACT_PREFIX}
 */
const UPLOAD_ARTIFACT_STEP_NAME = "Upload artifact";

/**
 * Prefix matched against {@link JobStep.uses} to identify
 * `actions/upload-artifact` steps.
 */
const UPLOAD_ARTIFACT_PREFIX = "actions/upload-artifact@";

/**
 * Value of the `with.name` input on the projen-emitted build-artifact
 * upload step. Matching all three of name + uses + with.name keeps the
 * patch surgical — other `actions/upload-artifact` steps (e.g. the
 * `turbo-runs` upload, the `repo.patch` upload) are left untouched.
 */
const BUILD_ARTIFACT_WITH_NAME = "build-artifact";

/**
 * Sets `with["include-hidden-files"] = true` on every build-artifact
 * `Upload artifact` step generated by projen's
 * {@code BuildWorkflow.renderBuildSteps()}.
 *
 * **Why this exists.** Projen's build workflow brackets the
 * `Upload artifact` step (for the `dist` build artifact) with a pair
 * of permission backup/restore steps:
 *
 * 1. `Backup artifact permissions` — runs
 *    `cd dist && getfacl -R . > permissions-backup.acl` and captures
 *    ACLs for **every** entry under `dist/`, including dotfiles
 *    (`.pnpm`, `.travis.yml`, etc.).
 * 2. `Upload artifact` — uploads `dist` using `actions/upload-artifact`.
 *    In `actions/upload-artifact` v4+, the default value of the
 *    `include-hidden-files` input is `false`, so every hidden entry is
 *    silently dropped from the artifact zip.
 * 3. `Restore build artifact permissions` (in each deploy job) — runs
 *    `setfacl --restore=permissions-backup.acl`. The ACL backup
 *    references hidden paths that no longer exist in the downloaded
 *    artifact, so `setfacl` exits non-zero, producing a failure
 *    annotation on every deploy job (`continue-on-error: true` masks
 *    the job result but does not suppress the annotation).
 *
 * Setting `include-hidden-files: true` on the upload keeps the artifact
 * faithful to what `getfacl -R .` captured, eliminating the
 * `setfacl --restore` errors and preserving hidden files that CDK
 * assets legitimately depend on (e.g. the `.pnpm` store layout in a
 * bundled `node_modules/`).
 *
 * **Why this is a configulator patch instead of an upstream projen
 * change.** Projen exposed the `includeHiddenFiles` opt-in in
 * `actions/upload-artifact` via
 * [projen#3827](https://github.com/projen/projen/pull/3827) but never
 * plumbed it into its own `BuildWorkflow.renderBuildSteps()`
 * build-artifact upload. Until projen patches its own helper, this
 * helper is the downstream fix.
 *
 * **Why this is a separate helper.** Projen's BuildWorkflow stores
 * the build job's `steps` as a **lazy closure**
 * (`steps: () => this.renderBuildSteps(...)`), not an eager array, so
 * the `Upload artifact` step does not exist anywhere at
 * `preSynthesize` time. The two existing sibling helpers
 * ({@code pinPnpmActionSetup}, {@code pinSetupNodeVersion}) reach
 * lazy-closure steps by patching the component-level
 * `preBuildSteps` / `postBuildSteps` arrays the closure later
 * concatenates — that path does not work here because the build-
 * artifact upload is emitted **inside** `renderBuildSteps()` and is
 * never added to either component-level array.
 *
 * Instead, this helper wraps each job's `steps` closure: it replaces
 * the original `() => JobStep[]` with a new closure that invokes the
 * original and post-processes the rendered array to set
 * `with["include-hidden-files"] = true` on every matching step.
 * Eager (already-array) step lists are patched in place.
 *
 * **Matching rule.** A step is patched only when all three of the
 * following are true:
 *
 * - `step.name === "Upload artifact"`
 * - `step.uses` starts with `actions/upload-artifact@`
 * - `step.with.name === "build-artifact"`
 *
 * This deliberately misses other `actions/upload-artifact` steps in
 * the same workflow (e.g. the `turbo-runs` artifact, the `repo.patch`
 * self-mutation artifact) — those uploads do not source hidden files
 * and do not pair with a `setfacl --restore` restore step.
 *
 * @param project - The project whose GitHub workflows should be patched.
 */
export function includeHiddenFilesInBuildArtifact(project: Project): void {
  const github = GitHub.of(project);
  if (!github) {
    return;
  }
  for (const workflow of github.workflows) {
    for (const job of Object.values(workflow.jobs)) {
      patchJobSteps(job);
    }
  }
}

/**
 * Mutates a job's `steps` field so any matching build-artifact upload
 * step carries `with["include-hidden-files"] = true`.
 *
 * Handles both the eager (`steps` is an array) and lazy (`steps` is a
 * closure returning an array) shapes projen uses for workflow jobs.
 */
function patchJobSteps(job: unknown): void {
  if (typeof job !== "object" || job === null) {
    return;
  }
  const mutable = job as { steps?: unknown };
  const steps = mutable.steps;
  if (Array.isArray(steps)) {
    patchStepArray(steps as Array<MutableStep>);
    return;
  }
  if (typeof steps === "function") {
    const original = steps as () => Array<MutableStep>;
    mutable.steps = () => {
      const rendered = original();
      patchStepArray(rendered);
      return rendered;
    };
  }
}

/**
 * Walks a step array in place and sets
 * `with["include-hidden-files"] = true` on every step that matches
 * the build-artifact upload shape documented in
 * {@link includeHiddenFilesInBuildArtifact}.
 */
function patchStepArray(steps: Array<MutableStep>): void {
  for (const step of steps) {
    if (isBuildArtifactUploadStep(step)) {
      if (!step.with || typeof step.with !== "object") {
        step.with = {};
      }
      step.with["include-hidden-files"] = true;
    }
  }
}

/**
 * Returns `true` when the step matches all three of:
 *
 * - `name === "Upload artifact"`
 * - `uses` starts with `actions/upload-artifact@`
 * - `with.name === "build-artifact"`
 */
function isBuildArtifactUploadStep(step: MutableStep): boolean {
  if (step.name !== UPLOAD_ARTIFACT_STEP_NAME) {
    return false;
  }
  if (
    typeof step.uses !== "string" ||
    !step.uses.startsWith(UPLOAD_ARTIFACT_PREFIX)
  ) {
    return false;
  }
  const withBlock = step.with;
  if (!withBlock || typeof withBlock !== "object") {
    return false;
  }
  return (withBlock as { name?: unknown }).name === BUILD_ARTIFACT_WITH_NAME;
}

/**
 * Internal mutable view of a {@link JobStep} so we can rewrite the
 * `with` map past projen's `readonly` declarations.
 */
interface MutableStep {
  name?: string;
  uses?: string;
  with?: Record<string, unknown>;
}
